import React, { useEffect, useState, useMemo } from "react";

import { Shield, Lock, Clock, AlertCircle } from "lucide-react";

import Pagination from "../../Components/Pagination";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchAuthLogs, AuthLog } from "../../Store/Reducers/AdminSlice";
import { diffTime } from "../../Utils/Helper/Helper";
import "./style.scss";

const AdminSecurityContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { authLogsData, authLogsLoading, authLogsError } = useAppSelector(
    (state: any) => state.admin
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    // Only fetch if auth logs are not already loaded
    if (!authLogsData) {
      dispatch(
        fetchAuthLogs({
          event_type: "login",
        }) as any
      );
    }
  }, [dispatch]);

  const formatTimestamp = (dateString: string): string => {
    if (!dateString) return "-";
    try {
      const diff = diffTime(dateString);
      // Convert "X minutes" to "X min ago" format
      if (diff.includes("minute")) {
        const minutes = diff.replace(" minutes", "").replace(" minute", "");
        return `${minutes} min ago`;
      }
      if (diff.includes("hour")) {
        const hours = diff.replace(" hours", "").replace(" hour", "");
        return `${hours} hr ago`;
      }
      if (diff.includes("day")) {
        const days = diff.replace(" days", "").replace(" day", "");
        return `${days} day${days !== "1" ? "s" : ""} ago`;
      }
      return diff;
    } catch {
      return dateString;
    }
  };

  const getEventTypeDisplay = (eventType: string): string => {
    if (eventType === "login") return "Login Attempt";
    if (eventType === "logout") return "Logout Attempt";
    return eventType.charAt(0).toUpperCase() + eventType.slice(1);
  };

  const securityPolicies = [
    { name: "Password Policy", status: "Enforced", statusColor: "green" },
    { name: "Multi-Factor Auth", status: "Optional", statusColor: "orange" },
    { name: "Session Timeout", status: "24 hours", statusColor: "green" },
  ];

  const auditLogs = authLogsData?.logs || [];

  // Calculate paginated data
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return auditLogs.slice(startIndex, endIndex);
  }, [auditLogs, currentPage, pageSize]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  if (authLogsError) {
    return (
      <div className="admin-security-container">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4">
            <div className="flex flex-col items-center justify-center min-h-[300px]">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Security Data
              </h3>
              <p className="text-sm text-gray-500 text-center">
                {authLogsError}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-security-container">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100">
              <Shield className="h-5 w-5 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Security & Compliance
            </h2>
          </div>

          {/* Security Policies Section */}
          <div className="mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-4 w-4 text-gray-500" />
              <h3 className="text-base font-semibold text-gray-900">
                Security Policies
              </h3>
            </div>
            <div className="flex flex-col gap-3">
              {securityPolicies.map((policy, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:shadow-sm transition-all duration-200"
                >
                  <span className="text-sm font-medium text-gray-900">
                    {policy.name}
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      policy.statusColor === "green"
                        ? "bg-green-100 text-green-700"
                        : "bg-orange-100 text-orange-700"
                    }`}
                  >
                    {policy.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Audit Logs Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-gray-500" />
              <h3 className="text-base font-semibold text-gray-900">
                Audit Logs
              </h3>
              {auditLogs.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  {auditLogs.length}{" "}
                  {auditLogs.length === 1 ? "entry" : "entries"}
                </span>
              )}
            </div>
            {authLogsLoading ? (
              <div className="flex flex-col items-center justify-center min-h-[300px]">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                <p className="text-sm text-gray-600 mt-4 font-medium">
                  Loading audit logs...
                </p>
              </div>
            ) : auditLogs.length > 0 ? (
              <>
                <div className="flex flex-col gap-2 mb-6">
                  {paginatedLogs.map((log: AuthLog, index: number) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:shadow-sm transition-all duration-200"
                    >
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 mb-1">
                          {getEventTypeDisplay(log.event_type)}
                        </span>
                        <span className="text-xs text-gray-500 truncate">
                          {log.email}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                        {formatTimestamp(log.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
                {auditLogs.length > pageSize && (
                  <Pagination
                    mode="static"
                    currentPage={currentPage}
                    pageSize={pageSize}
                    dataLength={auditLogs.length}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    className="mt-6"
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[200px] bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-200 mb-3">
                  <Clock className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  No audit logs found
                </p>
                <p className="text-xs text-gray-500">
                  Audit logs will appear here when available
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSecurityContainer;
